import { createFileRoute, redirect, useParams, useRouter, useSearch } from '@tanstack/react-router';
import { queryKeys, useGetIcpProductCategoryQuery, useGetProductCategoryQuery } from '@vizit/api';
import { FEATURE_FLAGS } from '@vizit/constants';
import { getFeatureFlag } from '@vizit/feature-flags';
import * as React from 'react';
import { ExploreClassifications } from '../../../../../pages';
import { PagesProvider } from '../../../../../pages/providers/pages-provider';
import { beforeLoadAuthenticatedValidation } from '../../../../../router/route-utils';
import { EXPLORE_BREADCRUMB } from '../../../../../router/route-utils/breadcrumbs';
import { NavbarPageTemplate } from '../../../../../templates';

export const Route = createFileRoute('/_authenticated/w/$workspaceSlug/explore/$categoryL1_/$categoryL4')({
  staticData: {
    pageName: 'Explore Classifications',
  },
  beforeLoad: async ({ context, location, params }) => {
    await beforeLoadAuthenticatedValidation(context, location);

    const isClipExperimentEnabled = await getFeatureFlag(FEATURE_FLAGS.TMP_CLIP_EXPERIMENT, false);
    if (isClipExperimentEnabled) {
      throw redirect({ to: '/w/$workspaceSlug/', params: { workspaceSlug: params.workspaceSlug } });
    }
  },
  validateSearch: (search: Record<string, unknown>): { exploreImageId?: string } => {
    return {
      exploreImageId: typeof search.exploreImageId === 'string' ? search.exploreImageId : undefined,
    };
  },
  loader: async ({ params, context }) => {
    const l1Category = await context.queryClient.ensureQueryData({
      queryKey: queryKeys.categories.icpProduct({ icp_product_category_id: params.categoryL1 }).queryKey,
      queryFn: () => useGetIcpProductCategoryQuery.fetcher({ icp_product_category_id: params.categoryL1 })(),
    });

    const l4Category = await context.queryClient.ensureQueryData({
      queryKey: queryKeys.categories.product({ category_id: params.categoryL4 }).queryKey,
      queryFn: () => useGetProductCategoryQuery.fetcher({ category_id: params.categoryL4 })(),
    });

    return {
      breadcrumbs: [
        EXPLORE_BREADCRUMB,
        {
          label: l1Category?.category_name ?? 'Category L1',
          href: `/w/${params.workspaceSlug}/explore/${params.categoryL1}`,
        },
        { label: l4Category?.name ?? 'Category L4' },
      ],
    };
  },
  component: RouteComponent,
});

function RouteComponent() {
  const { categoryL1, categoryL4, workspaceSlug } = useParams({ from: Route.id });
  const router = useRouter();
  const search = useSearch({ from: Route.id });

  const handleAssetClick = React.useCallback(
    (imageId: string) => {
      router.navigate({
        to: '/w/$workspaceSlug/explore/$categoryL1/$categoryL4',
        params: { workspaceSlug, categoryL1, categoryL4 },
        resetScroll: false,
        search: (prev: Record<string, unknown>) => ({
          ...prev,
          exploreImageId: imageId,
        }),
      });
    },
    [router, categoryL1, categoryL4, workspaceSlug],
  );

  const handleCloseModal = React.useCallback(() => {
    router.navigate({
      to: '/w/$workspaceSlug/explore/$categoryL1/$categoryL4',
      params: { workspaceSlug, categoryL1, categoryL4 },
      resetScroll: false,
      search: (prev: Record<string, unknown>) => {
        const { exploreImageId, ...rest } = prev;
        return rest;
      },
    });
  }, [router, categoryL1, categoryL4, workspaceSlug]);

  return (
    <PagesProvider template={NavbarPageTemplate}>
      <ExploreClassifications
        categoryIcp={categoryL1}
        categoryL4={categoryL4}
        onAssetClick={handleAssetClick}
        onCloseModal={handleCloseModal}
        exploreImageId={search.exploreImageId}
      />
    </PagesProvider>
  );
}
